import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AdvancedSearchTab } from "@/hooks/useAdvancedSearchTabs";
import { LinkGroup } from "./CellLinkDropdown";

interface AdvancedSearchOverviewProps {
  tabs: AdvancedSearchTab[];
  activeTabId: string;
  linkGroups: LinkGroup[];
  onTabClick: (tabId: string) => void;
  getFilteredResultCount: (tab: AdvancedSearchTab) => number;
}

// --- Layout algorithm types ---
interface Intersection {
  otherTabId: string;
  myCellIndex: number;
  otherCellIndex: number;
  color: string;
}

interface PlacedWord {
  tabId: string;
  startRow: number;
  startCol: number;
  direction: "horizontal" | "vertical";
  length: number;
}

interface GridCell {
  letter: string;
  tabIds: string[];
  cellIndices: Map<string, number>; // tabId → cellIndex
  intersectionColor?: string;
}

// --- Build intersection map from link groups ---
function buildIntersections(linkGroups: LinkGroup[]): Map<string, Intersection[]> {
  const map = new Map<string, Intersection[]>();

  for (const group of linkGroups) {
    for (let i = 0; i < group.cells.length; i++) {
      for (let j = i + 1; j < group.cells.length; j++) {
        const a = group.cells[i];
        const b = group.cells[j];
        if (a.tabId === b.tabId) continue;

        if (!map.has(a.tabId)) map.set(a.tabId, []);
        if (!map.has(b.tabId)) map.set(b.tabId, []);

        map.get(a.tabId)!.push({
          otherTabId: b.tabId,
          myCellIndex: a.cellIndex,
          otherCellIndex: b.cellIndex,
          color: group.color,
        });
        map.get(b.tabId)!.push({
          otherTabId: a.tabId,
          myCellIndex: b.cellIndex,
          otherCellIndex: a.cellIndex,
          color: group.color,
        });
      }
    }
  }

  return map;
}

// --- Get word length for a tab ---
function getWordLength(tab: AdvancedSearchTab): number {
  if (tab.selectedLength && tab.selectedLength > 0) return tab.selectedLength;
  const lastFilled = tab.letters.reduce((acc, l, i) => (l !== "" ? i : acc), -1);
  return lastFilled >= 0 ? lastFilled + 1 : 0;
}

// --- Check if placing a word would conflict with existing grid ---
function hasConflict(
  grid: Map<string, GridCell>,
  startRow: number,
  startCol: number,
  direction: "horizontal" | "vertical",
  length: number,
  tabId: string,
  letters: string[]
): boolean {
  for (let i = 0; i < length; i++) {
    const r = direction === "horizontal" ? startRow : startRow + i;
    const c = direction === "horizontal" ? startCol + i : startCol;
    const key = `${r},${c}`;
    const existing = grid.get(key);
    if (existing) {
      // If both have letters and they don't match → conflict
      const myLetter = letters[i]?.toUpperCase() || "";
      const existingLetter = existing.letter;
      if (myLetter && existingLetter && myLetter !== existingLetter) {
        return true;
      }
    }
  }
  return false;
}

// --- Place word cells into grid ---
function placeWordInGrid(
  grid: Map<string, GridCell>,
  placed: PlacedWord,
  tab: AdvancedSearchTab,
  intersectionColors: Map<string, string> // `tabId:cellIndex` → color
) {
  for (let i = 0; i < placed.length; i++) {
    const r = placed.direction === "horizontal" ? placed.startRow : placed.startRow + i;
    const c = placed.direction === "horizontal" ? placed.startCol + i : placed.startCol;
    const key = `${r},${c}`;
    const letter = tab.letters[i]?.toUpperCase() || "";
    const colorKey = `${tab.id}:${i}`;

    const existing = grid.get(key);
    if (existing) {
      existing.tabIds.push(tab.id);
      existing.cellIndices.set(tab.id, i);
      if (!existing.letter && letter) existing.letter = letter;
      if (intersectionColors.has(colorKey)) {
        existing.intersectionColor = intersectionColors.get(colorKey);
      }
    } else {
      const cell: GridCell = {
        letter,
        tabIds: [tab.id],
        cellIndices: new Map([[tab.id, i]]),
      };
      if (intersectionColors.has(colorKey)) {
        cell.intersectionColor = intersectionColors.get(colorKey);
      }
      grid.set(key, cell);
    }
  }
}

// --- Compute crossword layout via BFS ---
function computeLayout(
  tabs: AdvancedSearchTab[],
  linkGroups: LinkGroup[]
): {
  grid: Map<string, GridCell>;
  placedWords: Map<string, PlacedWord>;
  unplacedTabIds: string[];
  bounds: { minRow: number; maxRow: number; minCol: number; maxCol: number };
} {
  const intersections = buildIntersections(linkGroups);
  const tabMap = new Map(tabs.map((t) => [t.id, t]));
  const placedWords = new Map<string, PlacedWord>();
  const grid = new Map<string, GridCell>();

  // Build intersection color map
  const intersectionColors = new Map<string, string>();
  for (const group of linkGroups) {
    for (const cell of group.cells) {
      intersectionColors.set(`${cell.tabId}:${cell.cellIndex}`, group.color);
    }
  }

  // Sort tabs: most connected first for better layout
  const sortedTabs = [...tabs].sort((a, b) => {
    const aConns = intersections.get(a.id)?.length || 0;
    const bConns = intersections.get(b.id)?.length || 0;
    return bConns - aConns;
  });

  // BFS placement for each connected component
  for (const startTab of sortedTabs) {
    if (placedWords.has(startTab.id)) continue;

    const length = getWordLength(startTab);
    if (length === 0) continue;

    // Find a free row for disconnected words
    let startRow = 0;
    if (placedWords.size > 0) {
      // Place below existing grid
      let maxR = 0;
      for (const pw of placedWords.values()) {
        const endR = pw.direction === "vertical" ? pw.startRow + pw.length - 1 : pw.startRow;
        maxR = Math.max(maxR, endR);
      }
      startRow = maxR + 2; // gap
    }

    placedWords.set(startTab.id, {
      tabId: startTab.id,
      startRow,
      startCol: 0,
      direction: "horizontal",
      length,
    });
    placeWordInGrid(grid, placedWords.get(startTab.id)!, startTab, intersectionColors);

    // BFS from this starting tab
    const queue = [startTab.id];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const current = placedWords.get(currentId)!;
      const conns = intersections.get(currentId) || [];

      for (const conn of conns) {
        if (placedWords.has(conn.otherTabId)) continue;
        const otherTab = tabMap.get(conn.otherTabId);
        if (!otherTab) continue;

        const otherLength = getWordLength(otherTab);
        if (otherLength === 0) continue;

        const newDirection: "horizontal" | "vertical" =
          current.direction === "horizontal" ? "vertical" : "horizontal";

        // Calculate intersection point in grid space
        let intersectRow: number, intersectCol: number;
        if (current.direction === "horizontal") {
          intersectCol = current.startCol + conn.myCellIndex;
          intersectRow = current.startRow;
        } else {
          intersectRow = current.startRow + conn.myCellIndex;
          intersectCol = current.startCol;
        }

        // Calculate start position for new word
        let newStartRow: number, newStartCol: number;
        if (newDirection === "vertical") {
          newStartRow = intersectRow - conn.otherCellIndex;
          newStartCol = intersectCol;
        } else {
          newStartRow = intersectRow;
          newStartCol = intersectCol - conn.otherCellIndex;
        }

        // Check for grid conflicts before placing
        if (
          hasConflict(grid, newStartRow, newStartCol, newDirection, otherLength, conn.otherTabId, otherTab.letters)
        ) {
          continue;
        }

        const pw: PlacedWord = {
          tabId: conn.otherTabId,
          startRow: newStartRow,
          startCol: newStartCol,
          direction: newDirection,
          length: otherLength,
        };
        placedWords.set(conn.otherTabId, pw);
        placeWordInGrid(grid, pw, otherTab, intersectionColors);
        queue.push(conn.otherTabId);
      }
    }
  }

  // Calculate bounds
  let minRow = 0, maxRow = 0, minCol = 0, maxCol = 0;
  for (const key of grid.keys()) {
    const [r, c] = key.split(",").map(Number);
    minRow = Math.min(minRow, r);
    maxRow = Math.max(maxRow, r);
    minCol = Math.min(minCol, c);
    maxCol = Math.max(maxCol, c);
  }

  // Unplaced tabs
  const unplacedTabIds = tabs
    .filter((t) => !placedWords.has(t.id))
    .map((t) => t.id);

  return { grid, placedWords, unplacedTabIds, bounds: { minRow, maxRow, minCol, maxCol } };
}

// --- Word legend entry ---
function WordLegend({
  number,
  tab,
  isActive,
  direction,
  filteredCount,
  onClick,
}: {
  number: number;
  tab: AdvancedSearchTab;
  isActive: boolean;
  direction: "horizontal" | "vertical";
  filteredCount: number;
  onClick: () => void;
}) {
  const displayName = tab.clueQuery.trim() || tab.name.trim() || "Ny";
  const dirLabel = direction === "horizontal" ? "→" : "↓";

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 text-xs px-1.5 py-0.5 rounded transition-colors text-left",
        isActive
          ? "bg-accent font-semibold text-foreground"
          : "hover:bg-accent/50 text-muted-foreground"
      )}
    >
      <span className="font-mono text-[10px] text-muted-foreground w-4 text-right flex-shrink-0">
        {number}{dirLabel}
      </span>
      <span className="truncate max-w-[120px]">{displayName}</span>
      {tab.selectedLength && (
        <span className="text-[10px] text-muted-foreground/70">({tab.selectedLength})</span>
      )}
      <span
        className={cn(
          "ml-auto text-[10px] flex-shrink-0",
          filteredCount > 0 ? "text-muted-foreground" : "text-destructive/60"
        )}
      >
        {tab.hasSearched ? `${filteredCount}` : "–"}
      </span>
    </button>
  );
}

// --- Main component ---
export const AdvancedSearchOverview = ({
  tabs,
  activeTabId,
  linkGroups,
  onTabClick,
  getFilteredResultCount,
}: AdvancedSearchOverviewProps) => {
  const layout = useMemo(() => computeLayout(tabs, linkGroups), [tabs, linkGroups]);

  const { grid, placedWords, unplacedTabIds, bounds } = layout;
  const tabMap = useMemo(() => new Map(tabs.map((t) => [t.id, t])), [tabs]);

  // Assign numbers to placed words (for legend)
  const wordNumbers = useMemo(() => {
    const nums = new Map<string, number>();
    let n = 1;
    // Sort by position: top-to-bottom, left-to-right
    const sorted = [...placedWords.values()].sort((a, b) => {
      if (a.startRow !== b.startRow) return a.startRow - b.startRow;
      return a.startCol - b.startCol;
    });
    for (const pw of sorted) {
      nums.set(pw.tabId, n++);
    }
    return nums;
  }, [placedWords]);

  if (tabs.length === 0) return null;

  const CELL_SIZE = 22;
  const rows = bounds.maxRow - bounds.minRow + 1;
  const cols = bounds.maxCol - bounds.minCol + 1;

  // Check if we have any placed words at all
  const hasGrid = placedWords.size > 0;

  return (
    <div className="border rounded-lg bg-card/50 overflow-hidden">
      <ScrollArea className="max-h-[400px]">
        <div className="p-2 space-y-2">
          {/* Crossword grid */}
          {hasGrid && (
            <div className="overflow-auto">
              <div
                className="relative mx-auto"
                style={{
                  width: cols * CELL_SIZE,
                  height: rows * CELL_SIZE,
                }}
              >
                {Array.from(grid.entries()).map(([key, cell]) => {
                  const [r, c] = key.split(",").map(Number);
                  const top = (r - bounds.minRow) * CELL_SIZE;
                  const left = (c - bounds.minCol) * CELL_SIZE;
                  const isIntersection = cell.tabIds.length > 1;
                  const isActive = cell.tabIds.includes(activeTabId);

                  // Find word number at this cell (if it's the start of a word)
                  let wordNumber: number | null = null;
                  for (const tabId of cell.tabIds) {
                    const pw = placedWords.get(tabId);
                    if (pw) {
                      const ci = cell.cellIndices.get(tabId);
                      if (ci === 0) {
                        wordNumber = wordNumbers.get(tabId) || null;
                        break;
                      }
                    }
                  }

                  return (
                    <button
                      key={key}
                      className={cn(
                        "absolute border border-border flex items-center justify-center font-mono transition-colors",
                        isActive
                          ? "bg-accent/80 border-primary/50"
                          : "bg-background hover:bg-accent/40",
                        isIntersection && "ring-1 ring-inset"
                      )}
                      style={{
                        top,
                        left,
                        width: CELL_SIZE,
                        height: CELL_SIZE,
                        fontSize: cell.letter ? 11 : 9,
                        ...(cell.intersectionColor
                          ? {
                              ringColor: cell.intersectionColor,
                              boxShadow: `inset 0 0 0 1.5px ${cell.intersectionColor}`,
                            }
                          : {}),
                      }}
                      onClick={() => {
                        // Click navigates to the first tab at this cell (prefer active)
                        const targetId = cell.tabIds.includes(activeTabId)
                          ? cell.tabIds.find((id) => id !== activeTabId) || cell.tabIds[0]
                          : cell.tabIds[0];
                        onTabClick(targetId);
                      }}
                      title={cell.tabIds
                        .map((id) => {
                          const t = tabMap.get(id);
                          return t ? t.clueQuery.trim() || t.name.trim() || "Ny" : "";
                        })
                        .join(" × ")}
                    >
                      {wordNumber !== null && (
                        <span className="absolute top-px left-px text-[7px] leading-none text-muted-foreground font-sans">
                          {wordNumber}
                        </span>
                      )}
                      <span className={cn(
                        cell.letter ? "font-bold text-foreground" : "text-muted-foreground/30"
                      )}>
                        {cell.letter || "·"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend / word list */}
          <div className="space-y-0.5">
            {[...placedWords.values()]
              .sort((a, b) => (wordNumbers.get(a.tabId) || 0) - (wordNumbers.get(b.tabId) || 0))
              .map((pw) => {
                const tab = tabMap.get(pw.tabId);
                if (!tab) return null;
                return (
                  <WordLegend
                    key={pw.tabId}
                    number={wordNumbers.get(pw.tabId) || 0}
                    tab={tab}
                    isActive={pw.tabId === activeTabId}
                    direction={pw.direction}
                    filteredCount={getFilteredResultCount(tab)}
                    onClick={() => onTabClick(pw.tabId)}
                  />
                );
              })}

            {/* Unplaced tabs (no length set or isolated) */}
            {unplacedTabIds.length > 0 && (
              <>
                {placedWords.size > 0 && (
                  <div className="text-[10px] text-muted-foreground/60 pt-1 px-1.5">
                    Ej placerade:
                  </div>
                )}
                {unplacedTabIds.map((tabId) => {
                  const tab = tabMap.get(tabId);
                  if (!tab) return null;
                  const displayName = tab.clueQuery.trim() || tab.name.trim() || "Ny";
                  return (
                    <button
                      key={tabId}
                      onClick={() => onTabClick(tabId)}
                      className={cn(
                        "flex items-center gap-1.5 text-xs px-1.5 py-0.5 rounded transition-colors text-left w-full",
                        tabId === activeTabId
                          ? "bg-accent font-semibold text-foreground"
                          : "hover:bg-accent/50 text-muted-foreground"
                      )}
                    >
                      <span className="text-[10px] text-muted-foreground/50">–</span>
                      <span className="truncate">{displayName}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
